import React, { useState, useEffect, useRef } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { LANGUAGES } from '../languages/langs';

const variants = {
  dropdown: {
    container: "relative",
    trigger: "inline-flex items-center justify-between w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50",
    menu: "absolute right-0 mt-2 w-48 rounded-lg overflow-hidden bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 z-50",
    option: "flex items-center justify-between w-full px-4 py-2.5 text-sm transition-colors",
    optionActive: "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white",
    optionInactive: "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
  },
  select: {
    container: "relative",
    trigger: "inline-flex items-center justify-between w-32 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50",
    menu: "absolute right-0 mt-2 w-32 rounded-lg overflow-hidden bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 z-50",
    option: "flex items-center px-4 py-2.5 text-sm transition-colors",
    optionActive: "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white",
    optionInactive: "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
  },
  minimal: {
    container: "relative",
    trigger: "p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50",
    menu: "absolute right-0 mt-2 w-40 rounded-lg overflow-hidden bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 z-50",
    option: "flex items-center px-4 py-2.5 text-sm transition-colors",
    optionActive: "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white",
    optionInactive: "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
  }
};

const menuAnimation = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.15 }
};

const LanguageSelector = ({
  variant = 'dropdown',
  currentLanguage,
  onLanguageChange,
  disabled = false,
  className = ''
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  const styles = variants[variant] || variants.dropdown;
  const currentLang = LANGUAGES.find(lang => lang.code === currentLanguage);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current?.contains(event.target) &&
        !triggerRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    onLanguageChange(langCode);
    setIsOpen(false);
  };

  const renderTrigger = () => {
    if (variant === 'minimal') {
      return <Globe className="w-5 h-5" />;
    }

    return (
      <>
        <span className="mr-2">{currentLang ? t(`settings.languages.${currentLang.code}`) : ''}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </>
    );
  };

  return (
    <div className={`${styles.container} ${className}`}>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={styles.trigger}
        aria-label={t('navbar.language')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {renderTrigger()}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            role="menu"
            {...menuAnimation}
            className={styles.menu}
          >
            <div className="py-1">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`${styles.option} ${currentLanguage === lang.code
                      ? styles.optionActive
                      : styles.optionInactive
                    }`}
                  role="menuitem"
                >
                  <span>{t(`settings.languages.${lang.code}`)}</span>
                  {currentLanguage === lang.code && (
                    <Check className="w-4 h-4 ml-2" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;